import Vue, { CreateElement } from '@src/vue'
import { Component, Prop, Watch } from 'vue-property-decorator'

import { CHART_LABEL_FONT_STYLE, CHART_LABEL_TWO_ROWS_FONT_STYLE, MAX_MOBILE_WIDTH } from '@src/util/constants'
import { ErrorScreen } from '@src/components/error-screen/error-screen'
import { convertToLocalDate } from '@src/util/date-format'
import { displayDateForChart } from '@src/util/functions'
import VueApexCharts from 'vue-apexcharts'
import './chart.sass'
import { getChartRef } from '@src/components/chart/utils'

Vue.component('apexchart', VueApexCharts)

@Component({
  computed: {
    locale () {
      return this.$store.state.i18n.locale
    }
  }
})
export class Chart extends Vue {

  @Prop({ required: true })
  series: any[]

  @Prop({ required: false, default: '' })
  emptyGraphMessage: string

  @Prop({ required: false, default: () => CHART_LABEL_FONT_STYLE })
  labelStyle: object

  @Prop({ required: false, default: 'month' })
  dateType: 'month' | 'day'

  locale

  @Prop({ required: true })
  chartId: string

  @Prop({ required: false })
  maxOY: number

  @Prop({ required: false, default: undefined })
  grid: any

  private portraitMatch: any
  private initialChartWidth: any = '100%'
  private initialChartHeight: number = 300

  constructor () {
    super()
  }

  mounted () {
    this.portraitMatch = window.matchMedia('(orientation: portrait)')
    this.portraitMatch.addEventListener('change', this.updateComponentOnOrientationChange)
    window.addEventListener('beforeprint', this.beforePrint)
    window.addEventListener('afterprint', this.afterPrint)
    window.addEventListener('resize', this.resize)
  }

  beforeDestroy () {
    this.portraitMatch.removeEventListener('change', this.updateComponentOnOrientationChange)
    window.removeEventListener('beforeprint', this.beforePrint)
    window.removeEventListener('afterprint', this.afterPrint)
    window.removeEventListener('resize', this.resize)
  }

  resize () {
    const chart = getChartRef(this.$refs, this.chartId)
    chart.updateSeries(this.series, true).then(() => {
      this.$forceUpdate()
    })
  }

  @Watch('locale')
  updateChartOnLocaleUpdate () {
    const chart = getChartRef(this.$refs, this.chartId)
    if (!chart) return
    chart.render()
  }

  @Watch('series')
  updateChartOnSeriesChange () {
    const chart = getChartRef(this.$refs, this.chartId)
    if (!chart) return
    chart.updateSeries(this.series, true)
  }

  updateComponentOnOrientationChange () {
    this.$forceUpdate()
  }

  async beforePrint () {
    const chart = getChartRef(this.$refs, this.chartId)
    if (!chart) return

    const printWidth = 650
    const printHeight = 300
    this.initialChartWidth = this.initialChartWidth || chart.opts.chart.width
    this.initialChartHeight = this.initialChartHeight || chart.opts.chart.height
    await chart.updateOptions({
      chart: {
        width: printWidth,
        height: printHeight,
        animations: {
          enabled: false
        }
      }
    }, true, true, true)
  }

  async afterPrint () {
    const chart = getChartRef(this.$refs, this.chartId)
    if (!chart) return

    await chart.updateOptions({
      chart: {
        width: this.initialChartWidth,
        height: this.initialChartHeight,
        animations: {
          enabled: false
        }
      }
    }, true, true, true)
  }

  render (h: CreateElement) {
    if (this.series.length === 0 || this.series.filter(element => element.data.length > 0).length === 0) {
      return <ErrorScreen icon='brand/empty-graph' message={this.emptyGraphMessage} />
    }

    return (
      <div>

      <apexchart
        key={this.chartId}
        id={this.chartId}
        ref={this.chartId}
        height={this.initialChartHeight}
        options={this.getChartOptions()} series={this.series} />
      </div>
    )
  }

  getChartOptions () {
    return {
      chart: {
        type: 'line',
        height: 300,
        width: '100%',
        redrawOnParentResize: true,
        redrawOnWindowResize: true,
        zoom: {
          enabled: false
        },
        toolbar: {
          show: false
        },
        animations: {
          enabled: false
        }
      },
      grid: this.grid || {
        padding: {
          left: 15,
          right: 5
        }
      },
      markers: {
        size: 7,
        strokeWidth: 2,
        strokeOpacity: 0.9,
        strokeDashArray: 0,
        shape: 'circle'
      },
      stroke: {
        width: 1.5,
        curve: 'smooth'
      },
      legend: {
        show: false
      },
      tooltip: {
        onDatasetHover: {
          highlightDataSeries: true
        },
        shared: false,
        intersect: true,
        x: {
          show: true,
          formatter: function (value) {
            return convertToLocalDate(value)
          }
        }
      },
      yaxis: {
        opposite: true,
        forceNiceScale: false,
        decimalsInFloat: 0,
        max: this.maxOY || undefined,
        labels: {
          style: { ...this.labelStyle },
          formatter: (val) => val.toFixed(0)
        }
      },
      xaxis: {
        type: 'datetime',
        tickPlacement: 'between',
        tooltip: {
          enabled: false
        },
        tickAmount: window.innerWidth < MAX_MOBILE_WIDTH ? 4 : undefined,
        labels: {
          style: CHART_LABEL_TWO_ROWS_FONT_STYLE,
          showDuplicates: false,
          formatter: (val) => {
            return displayDateForChart({ value: val }, this.dateType, true)
          }
        }
      }
    }
  }
}
